import { notify, getApiErrorMessage } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Ban, RotateCcw, Percent } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

const FBR_SCENARIO_OPTIONS = [
  { label: 'trScenarioAuto', value: '' },
  { label: 'SN001 — Registered buyer, standard rate (18%)', value: 'SN001' },
  { label: 'SN002 — Unregistered buyer, standard rate (18%)', value: 'SN002' },
  { label: 'SN005 — Reduced rate goods [327(I)/2008]', value: 'SN005' },
  { label: 'SN006 — Exempt goods [Section 49]', value: 'SN006' },
  { label: 'SN007 — Zero-rated exports [FIFTH SCHEDULE]', value: 'SN007' },
  { label: 'SN008 — Fixed/notified retail price goods', value: 'SN008' },
  { label: 'SN016 — Processing/conversion of goods', value: 'SN016' },
  { label: 'SN017 — FED in ST mode', value: 'SN017' },
  { label: 'SN024 — SRO listed goods [327(I)/2008]', value: 'SN024' },
  { label: 'SN026 — Retail sale (end consumer)', value: 'SN026' },
  { label: 'SN027 — Further tax applicable', value: 'SN027' },
  { label: 'SN028 — Extra tax applicable', value: 'SN028' },
];

const defaultForm = {
  name: '',
  rate: '',
  type: 'EXCLUSIVE' as 'INCLUSIVE' | 'EXCLUSIVE',
  description: '',
  isDefault: false,
  fbrSroScheduleNo: '',
  fbrScenarioId: '',
};

interface TaxRate {
  id: string;
  name: string;
  rate: number | string;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
  description?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
  fbrSroScheduleNo?: string | null;
  fbrScenarioId?: string | null;
  _count?: { products: number; categories: number };
}

interface TaxRatesPagedResponse {
  data?: TaxRate[];
  meta?: { total?: number };
  total?: number;
}

type TaxRatesResponse = TaxRate[] | TaxRatesPagedResponse;

interface FbrSroSchedule {
  sroDesc: string;
}

interface TaxRatePayload {
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
  description?: string;
  isDefault: boolean;
  fbrSroScheduleNo?: string;
  fbrScenarioId?: string;
  isActive?: boolean;
  confirmInUse?: boolean;
}

/** POS-I344: the same bound the server enforces. */
const DESCRIPTION_MAX = 300;

export function TaxRatesPage() {
  const { t } = useTranslation('fbr');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('tax-rates:create') || hasPermission('tax-rates:*');
  const canUpdate = hasPermission('tax-rates:update') || hasPermission('tax-rates:*');
  const canDelete = hasPermission('tax-rates:delete') || hasPermission('tax-rates:*');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read');

  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState<{ name?: string; rate?: string; description?: string }>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<TaxRate | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [isActive, setIsActive] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; usage: string }>({ open: false, id: '', name: '', usage: '' });
  const queryClient = useQueryClient();

  useEffect(() => {
    setErrors({});
    if (dialogOpen && editItem) {
      setForm({
        name: editItem.name ?? '',
        rate: editItem.rate != null ? String(editItem.rate) : '',
        type: editItem.type ?? 'EXCLUSIVE',
        description: editItem.description ?? '',
        isDefault: editItem.isDefault ?? false,
        fbrSroScheduleNo: editItem.fbrSroScheduleNo ?? '',
        fbrScenarioId: editItem.fbrScenarioId ?? '',
      });
      setIsActive(editItem.isActive ?? true);
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
      setIsActive(true);
    }
  }, [dialogOpen, editItem]);

  const { data: taxRates, isLoading } = useQuery<TaxRatesResponse>({
    queryKey: ['tax-rates', page, search],
    queryFn: async () => {
      const res = await api.get('/tax-rates', { params: { page, limit, includeInactive: true, ...(search.trim() ? { search: search.trim() } : {}) } });
      return res.data;
    },
  });

  const { data: fbrSroSchedules } = useQuery<FbrSroSchedule[]>({
    queryKey: ['fbr-reference-sro'],
    queryFn: async () => { const res = await api.get('/fbr/reference/sro-schedules'); return res.data; },
    staleTime: 1000 * 60 * 60,
    enabled: canViewFbr,
  });

  const sroOptions = [
    { label: t('trSroNone'), value: '' },
    ...(fbrSroSchedules ?? [])
      .map((s: FbrSroSchedule) => ({ label: s.sroDesc, value: s.sroDesc }))
      .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i)
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const usageOf = (item: TaxRate | null | undefined): { products: number; categories: number } => ({
    products: item?._count?.products ?? 0,
    categories: item?._count?.categories ?? 0,
  });
  const usageText = (item: TaxRate | null | undefined): string => {
    const u = usageOf(item);
    return t('trUsedByCell', { products: u.products, categories: u.categories });
  };
  const inUse = (item: TaxRate | null | undefined) => {
    const u = usageOf(item);
    return u.products > 0 || u.categories > 0;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: TaxRatePayload = {
        name: form.name,
        rate: Number(form.rate),
        type: form.type,
        description: editItem ? form.description.trim() : form.description.trim() || undefined,
        isDefault: form.isDefault,
        fbrSroScheduleNo: form.fbrSroScheduleNo || undefined,
        fbrScenarioId: form.fbrScenarioId || undefined,
      };
      if (editItem) {
        payload.isActive = isActive;
        // POS-I341: the owner saw the impact text beside the switch before saving.
        if (!isActive && editItem.isActive !== false) payload.confirmInUse = true;
        const { data } = await api.patch(`/tax-rates/${editItem.id}`, payload);
        return data;
      }
      const { data } = await api.post('/tax-rates', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editItem ? t('trUpdated') : t('trCreated'));
      queryClient.invalidateQueries({ queryKey: ['tax-rates'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('trSaveFailed')));
    },
  });

  const deleteMutation = useMutation({
    // POS-I341: the confirm dialog showed the counts, so the request carries the confirmation.
    mutationFn: (id: string) => api.delete(`/tax-rates/${id}`, { params: { confirmInUse: true } }),
    onSuccess: () => {
      toast.success(t('trDeactivated'));
      queryClient.invalidateQueries({ queryKey: ['tax-rates'] });
    },
    onError: (err) => notify.error(err, t('trActionFailed')),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/tax-rates/${id}`, { isActive: true }),
    onSuccess: () => {
      toast.success(t('trReactivated'));
      queryClient.invalidateQueries({ queryKey: ['tax-rates'] });
    },
    onError: (err) => notify.error(err, t('trActionFailed')),
  });

  const validate = (): boolean => {
    const next: { name?: string; rate?: string; description?: string } = {};
    if (!form.name.trim()) next.name = t('trErrName');
    const rateNum = Number(form.rate);
    if (form.rate.trim() === '' || !Number.isFinite(rateNum) || rateNum < 0 || rateNum > 100 || !/^\d{1,3}(\.\d{1,2})?$/.test(form.rate.trim())) {
      next.rate = t('trErrRate');
    }
    if (form.description.trim().length > DESCRIPTION_MAX || /[<>]/.test(form.description)) next.description = t('trErrDesc');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const pagedRates = Array.isArray(taxRates) ? undefined : taxRates;
  const list: TaxRate[] = Array.isArray(taxRates) ? taxRates : pagedRates?.data ?? [];
  const taxTotal: number = pagedRates?.meta?.total ?? pagedRates?.total ?? list.length;

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('trTitle')}
        subtitle={t('trSubtitle')}
        action={
          canCreate ? (
          <Button
            variant="gradient"
            onClick={() => { setEditItem(null); setDialogOpen(true); }}
          >
            <Plus className="me-2 h-4 w-4" />
            {t('trAdd')}
          </Button>
          ) : undefined
        }
      />

      {/* Tax rates table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <div className="flex justify-end p-3">
            <SearchInput
              value={search}
              onChange={(value) => { setSearch(value); setPage(1); }}
              placeholder={t('trSearch')}
              className="w-64"
            />
          </div>
          <Table mobileCards className="md:[&_th]:px-2.5 md:[&_td]:px-2.5">
            <THead>
              <Tr hover={false}>
                <Th>{t('tblTaxName')}</Th>
                <Th>{t('tblRate')}</Th>
                <Th>{t('tblType')}</Th>
                {canViewFbr && <Th>{t('tblFBRSchedule')}</Th>}
                <Th>{t('tblUsedBy')}</Th>
                <Th className="hidden xl:table-cell">{t('tblDescription')}</Th>
                <Th className="text-center">{t('tblDefault')}</Th>
                <Th className="text-center">{t('tblActive')}</Th>
                <Th className="text-end md:sticky md:end-0 md:z-10 md:bg-card">{t('tblActions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={9} className="py-8">
                    <SkeletonTable rows={5} cols={9} />
                  </Td>
                </Tr>
              ) : list.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={9} className="p-0">
                    <EmptyState
                      icon={Percent}
                      title={search.trim() ? t('trNoMatch') : t('trEmptyTitle')}
                      description={search.trim() ? '' : t('trEmptyDesc')}
                      action={canCreate && !search.trim() ? { label: t('trAdd'), onClick: () => { setEditItem(null); setDialogOpen(true); } } : undefined}
                    />
                  </Td>
                </Tr>
              ) : (
                list.map((item: TaxRate) => (
                  <Tr key={item.id}>
                    <Td className="font-medium td-name">{item.name}</Td>
                    <Td label={t('tblRate')}>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary-text">
                        <Percent className="h-3 w-3" />
                        {Number(item.rate)}%
                      </span>
                    </Td>
                    <Td label={t('tblType')}>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          item.type === 'INCLUSIVE'
                            ? 'bg-success/10 text-success-text'
                            : 'bg-info/10 text-info-text'
                        }`}
                      >
                        {item.type === 'INCLUSIVE' ? t('trInclusive') : t('trExclusive')}
                      </span>
                    </Td>
                    {canViewFbr && (
                    <Td label={t('tblFBRSchedule')} className="text-xs text-muted-foreground">
                      {item.fbrSroScheduleNo ? (
                        <span className="inline-flex items-center rounded bg-warning/10 px-1.5 py-0.5 text-warning-text font-mono text-xs border border-warning/30">
                          {item.fbrSroScheduleNo}
                        </span>
                      ) : '—'}
                    </Td>
                    )}
                    <Td label={t('tblUsedBy')} className="text-xs text-muted-foreground">
                      {inUse(item) ? usageText(item) : t('trNotUsed')}
                    </Td>
                    <Td label={t('tblDescription')} className="hidden xl:table-cell text-muted-foreground td-hide">
                      {/* POS-I344: at most two lines with an ellipsis; the full text is in the tooltip and the edit dialog */}
                      <span className="line-clamp-2 [overflow-wrap:anywhere]" title={item.description ?? undefined}>{item.description || '—'}</span>
                    </Td>
                    <Td label={t('tblDefault')} className="text-center">
                      {item.isDefault ? (
                        <span className="text-success-text">✓</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td label={t('tblActive')} className="text-center">
                      {item.isActive !== false ? (
                        <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-text">{t('trActive')}</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive-text">{t('trInactive')}</span>
                      )}
                    </Td>
                    <Td className="text-end td-actions md:sticky md:end-0 md:z-10 md:bg-card">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('trEdit'),
                            icon: Edit,
                            onSelect: () => { setEditItem(item); setDialogOpen(true); },
                            permission: canUpdate,
                            primary: true,
                          },
                          // POS-I342: the menu says what the action does; an inactive rate offers Reactivate
                          item.isActive !== false
                            ? {
                                key: 'deactivate',
                                label: t('trDeactivate'),
                                icon: Ban,
                                onSelect: () => setDeleteConfirm({ open: true, id: item.id, name: item.name, usage: inUse(item) ? usageText(item) : '' }),
                                permission: canDelete,
                                variant: 'destructive' as const,
                              }
                            : {
                                key: 'reactivate',
                                label: t('trReactivate'),
                                icon: RotateCcw,
                                onSelect: () => reactivateMutation.mutate(item.id),
                                permission: canUpdate,
                              },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {taxTotal > limit && (
        <div className="border-t px-4 py-3 mt-1">
          <PaginationBar page={page} pageSize={limit} total={taxTotal} onPageChange={setPage} />
        </div>
      )}
      <FormDialog
        open={dialogOpen}
        title={editItem ? t('trEditTitle') : t('trAddTitle')}
        noValidate
        width="52rem"
        submitLabel={editItem ? t('trUpdateBtn') : t('trCreateBtn')}
        isPending={saveMutation.isPending}
        onSubmit={(e) => {
          e.preventDefault();
          if (!validate()) return;
          saveMutation.mutate();
        }}
        onClose={() => setDialogOpen(false)}
      >
        <div className="space-y-4">
          <div>
            <Label>{t('trName')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('trNamePh')}
              maxLength={100}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="mt-1 text-xs text-destructive-text" role="alert">{errors.name}</p>}
          </div>

          <div>
            <Label>{t('trRate')} *</Label>
            <Input
              type="number"
              value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              placeholder="0.00"
              min={0}
              max={100}
              step={0.01}
              aria-invalid={!!errors.rate}
            />
            {errors.rate && <p className="mt-1 text-xs text-destructive-text" role="alert">{errors.rate}</p>}
          </div>

          <div>
            <Label>{t('trType')}</Label>
            <Dropdown
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.value as 'INCLUSIVE' | 'EXCLUSIVE' })}
              options={[
                { label: t('trTypeExcl'), value: 'EXCLUSIVE' },
                { label: t('trTypeIncl'), value: 'INCLUSIVE' },
              ]}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('trTypeHelp')}
            </p>
          </div>

          <div>
            <Label>{t('tblDescription')}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('trDescPh')}
              rows={3}
              aria-invalid={!!errors.description}
            />
            {errors.description && <p className="mt-1 text-xs text-destructive-text" role="alert">{errors.description}</p>}
          </div>

          {canViewFbr && (
          <div className="border rounded-lg p-3 space-y-3 bg-warning/10 border-warning/30">
            <p className="text-xs font-semibold text-warning-text uppercase tracking-wide">{t('trFbrHead')}</p>
            <p className="text-xs text-muted-foreground">{t('trFbrHelp')}</p>

            <div>
              <Label className="text-xs">{t('trSro')}</Label>
              <Dropdown
                value={form.fbrSroScheduleNo}
                onChange={(e) => setForm({ ...form, fbrSroScheduleNo: e.value })}
                options={sroOptions}
                placeholder={t('trSroPh')}
                className="w-full"
                showClear
              />
              <p className="text-xs text-muted-foreground mt-1">{t('trSroHint')}</p>
            </div>

            <div>
              <Label className="text-xs">{t('trScenario')}</Label>
              <Dropdown
                value={form.fbrScenarioId}
                onChange={(e) => setForm({ ...form, fbrScenarioId: e.value || '' })}
                options={FBR_SCENARIO_OPTIONS.map((o) => ({ ...o, label: o.label === 'trScenarioAuto' ? t('trScenarioAuto') : o.label }))}
                placeholder={t('trScenarioPh')}
                className="w-full"
                showClear
              />
            </div>
          </div>
          )}

          <Label className="flex items-center gap-2 cursor-pointer font-normal leading-normal text-foreground">
            <Checkbox
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
            />
            <span className="text-sm font-medium">{t('trIsDefault')}</span>
            <span className="text-xs text-muted-foreground">{t('trIsDefaultHelp')}</span>
          </Label>

          {editItem && (
            <Label className="flex items-center gap-2 cursor-pointer font-normal leading-normal text-foreground">
              <Checkbox
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="text-sm font-medium">{t('trIsActive')}</span>
              {!isActive && editItem.isActive !== false && inUse(editItem) && (
                <span className="text-xs text-warning-text">{t('trIsActiveInUse', { usage: usageText(editItem) })}</span>
              )}
            </Label>
          )}
        </div>
      </FormDialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('trDeactivateTitle')}
        message={deleteConfirm.usage
          ? t('trDeactivateInUse', { name: deleteConfirm.name, usage: deleteConfirm.usage })
          : t('trDeactivateMsg', { name: deleteConfirm.name })}
        confirmLabel={t('trDeactivate')}
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '', usage: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '', usage: '' })}
      />
    </div>
  );
}
